import { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, Table, Badge, Spinner, Button, Nav } from 'react-bootstrap';
import { ApiService } from '../services/api';
import { WorkloadMetrics as WorkloadMetricsType } from '../types';
import { getStatusColor, getStatusIcon, formatTimestamp, formatFullTimestamp } from '../utils/reporting';

interface WorkloadMetricsProps {
  workloadName: string;
  onClose: () => void;
}

export function WorkloadMetrics({ workloadName, onClose }: WorkloadMetricsProps) {
  const [metrics, setMetrics] = useState<WorkloadMetricsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeEnvironment, setActiveEnvironment] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, [workloadName]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const metricsData = await ApiService.getWorkloadMetrics(workloadName);
      setMetrics(metricsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Helper to format customization type name
  const formatCustomizationType = (type: string): string => {
    const typeMap: Record<string, string> = {
      account: 'Account',
      cdkBootstrap: 'CDK Bootstrap',
      oidcRoles: 'OIDC Roles',
      hostedZones: 'Hosted Zones',
      sftpService: 'SFTP Service',
    };
    return typeMap[type] || type;
  };



  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-chart-line me-2"></i>
          Workload Metrics: {workloadName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading metrics...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <h6>Error Loading Metrics</h6>
            <p className="mb-0">{error}</p>
            <Button variant="outline-danger" size="sm" className="mt-2" onClick={loadData}>
              Retry
            </Button>
          </div>
        )}

        {metrics && (
          <>
            {/* Current Status */}
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <strong>Current Status:</strong>{' '}
                    <Badge bg={getStatusColor(metrics.currentStatus)}>
                      {getStatusIcon(metrics.currentStatus)} {metrics.currentStatus}
                    </Badge>
                  </Col>
                  <Col md={6}>
                    <strong>Last Deployed:</strong>{' '}
                    <span title={formatFullTimestamp(metrics.lastDeployed)}>
                      {formatTimestamp(metrics.lastDeployed)}
                    </span>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Deployment Statistics */}
            <h6 className="mb-3">
              <i className="fas fa-chart-bar me-2"></i>
              Deployment Statistics ({metrics.period})
            </h6>
            <Row className="mb-3">
              <Col md={3} sm={6} className="mb-3">
                <Card className="text-center h-100">
                  <Card.Body>
                    <h4 className="mb-1">{metrics.metrics.deployments.total}</h4>
                    <small className="text-muted">Total</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-3">
                <Card className="text-center border-success h-100">
                  <Card.Body>
                    <h4 className="text-success mb-1">{metrics.metrics.deployments.successful}</h4>
                    <small className="text-muted">Successful</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-3">
                <Card className="text-center border-danger h-100">
                  <Card.Body>
                    <h4 className="text-danger mb-1">{metrics.metrics.deployments.failed}</h4>
                    <small className="text-muted">Failed</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-3">
                <Card className="text-center border-info h-100">
                  <Card.Body>
                    <h4 className="text-info mb-1">{metrics.metrics.deployments.successRate}%</h4>
                    <small className="text-muted">Success Rate</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Performance */}
            <Card className="mb-3">
              <Card.Body>
                <h6 className="mb-2">
                  <i className="fas fa-tachometer-alt me-2"></i>
                  Performance
                </h6>
                <p className="mb-0">
                  <strong>Average Deploy Time:</strong> {metrics.metrics.performance.avgDeployTime}
                </p>
              </Card.Body>
            </Card>

            {/* Environment Tabs */}
            {(() => {
              const environments = ['all', ...new Set(metrics.recentDeployments.map((d: any) => d.environment))];
              const filteredDeployments = activeEnvironment === 'all' 
                ? metrics.recentDeployments 
                : metrics.recentDeployments.filter((d: any) => d.environment === activeEnvironment);
              
              return (
                <>
                  <Nav variant="tabs" className="mb-3">
                    {environments.map((env) => (
                      <Nav.Item key={env}>
                        <Nav.Link 
                          active={activeEnvironment === env}
                          onClick={() => setActiveEnvironment(env)}
                          style={{ cursor: 'pointer' }}
                        >
                          {env === 'all' ? 'All Environments' : env.toUpperCase()}
                          <Badge bg="secondary" className="ms-2">
                            {env === 'all' 
                              ? metrics.recentDeployments.length 
                              : metrics.recentDeployments.filter((d: any) => d.environment === env).length
                            }
                          </Badge>
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                  
                  {/* Deployment History */}
                  <h6 className="mb-2">
                    <i className="fas fa-history me-2"></i>
                    Deployment History
                  </h6>
                  <div className="table-responsive mb-4">
                    <Table striped size="sm">
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Type</th>
                          {activeEnvironment === 'all' && <th>Environment</th>}
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Deployed By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeployments.length === 0 ? (
                          <tr>
                            <td colSpan={activeEnvironment === 'all' ? 6 : 5} className="text-center text-muted">
                              No deployment history available
                            </td>
                          </tr>
                        ) : (
                          filteredDeployments.map((deployment, idx) => (
                            <tr key={idx}>
                              <td>
                                <small title={formatFullTimestamp(deployment.timestamp)}>
                                  {formatTimestamp(deployment.timestamp)}
                                </small>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {(deployment as any).type || 'Deployment'}
                                </small>
                              </td>
                              {activeEnvironment === 'all' && (
                                <td>
                                  <Badge bg="secondary" className="text-uppercase">
                                    {(deployment as any).environment || 'unknown'}
                                  </Badge>
                                </td>
                              )}
                              <td>
                                <Badge bg={getStatusColor(deployment.status)}>
                                  {getStatusIcon(deployment.status)} {deployment.status}
                                </Badge>
                              </td>
                              <td>
                                <small>{deployment.duration || '-'}</small>
                              </td>
                              <td>
                                <small>{deployment.deployedBy || '-'}</small>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Deployed Customizations */}
                  <h6 className="mb-2">
                    <i className="fas fa-puzzle-piece me-2"></i>
                    Deployed Customizations
                  </h6>
                  
                  {metrics?.customizationsError && (
                    <div className="alert alert-warning mb-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Unable to load customizations:</strong> {metrics.customizationsError}
                      <div className="mt-2">
                        <small className="text-muted">
                          Check Lambda logs for details. The workload configuration may not be accessible.
                        </small>
                      </div>
                    </div>
                  )}
                  
                  {metrics?.customizations && metrics.customizations.length > 0 && (
                    <Card className="mb-3">
                      <Card.Body>
                        <Table size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Environment</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {metrics.customizations
                                .filter((c) => activeEnvironment === 'all' || c.environment.toLowerCase() === activeEnvironment.toLowerCase())
                                .map((customization, idx) => {
                                  const statusColor = customization.status === 'deployed' ? 'success' : 'secondary';
                                  const statusIcon = customization.status === 'deployed' ? 'fa-check-circle' : 'fa-clock';
                                  const statusText = customization.status === 'deployed' ? 'Deployed' : 'Configured';

                                  return (
                                    <tr key={idx}>
                                      <td>
                                        <strong>{formatCustomizationType(customization.type)}</strong>
                                        <div className="mt-1">
                                          <Badge bg={statusColor}>
                                            <i className={`fas ${statusIcon} me-1`}></i>
                                            {statusText}
                                          </Badge>
                                        </div>
                                      </td>
                                      <td>
                                        <Badge bg={customization.environment === 'account-vending' ? 'info' : 'secondary'} className="text-uppercase">
                                          {customization.environment === 'account-vending' ? 'Account' : customization.environment}
                                        </Badge>
                                      </td>
                                      <td>
                                        {/* Account details */}
                                        {customization.details.accountName && (
                                          <div><strong>Name:</strong> {customization.details.accountName}</div>
                                        )}
                                        {customization.details.accountEmail && (
                                          <div><strong>Email:</strong> {customization.details.accountEmail}</div>
                                        )}
                                        {customization.details.accountId && (
                                          <div><strong>Account ID:</strong> {customization.details.accountId}</div>
                                        )}
                                        {customization.details.organizationalUnit && (
                                          <div><strong>OU:</strong> {customization.details.organizationalUnit}</div>
                                        )}

                                        {/* CDK Bootstrap details */}
                                        {customization.details.regions && (
                                          <div><strong>Regions:</strong> {customization.details.regions.join(', ')}</div>
                                        )}
                                        {customization.details.trustedAccounts && (
                                          <div><strong>Trusted Accounts:</strong> {customization.details.trustedAccounts.join(', ')}</div>
                                        )}

                                        {/* OIDC details */}
                                        {customization.details.gitlabProjectId && (
                                          <div><strong>GitLab Project:</strong> {customization.details.gitlabProjectId}</div>
                                        )}

                                        {/* Hosted Zones details */}
                                        {customization.details.zones && customization.details.zones.length > 0 && (
                                          <div><strong>Zones:</strong> {customization.details.zones.map((z: any) => z.domainName).join(', ')}</div>
                                        )}

                                        {/* SFTP details */}
                                        {customization.details.enabled !== undefined && (
                                          <div><strong>Enabled:</strong> {customization.details.enabled ? 'Yes' : 'No'}</div>
                                        )}

                                        {/* Region (common) */}
                                        {customization.details.region && !customization.details.regions && (
                                          <div><strong>Region:</strong> {customization.details.region}</div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}

                              {/* Show message if no customizations for this environment */}
                              {activeEnvironment !== 'all' && 
                                metrics.customizations.filter((c) => c.environment.toLowerCase() === activeEnvironment.toLowerCase()).length === 0 && (
                                <tr>
                                  <td colSpan={3} className="text-center text-muted">
                                    No customizations for {activeEnvironment.toUpperCase()} environment
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                  )}
                  
                  {!metrics?.customizationsError && (!metrics?.customizations || metrics.customizations.length === 0) && (
                    <div className="alert alert-info mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      No customizations configured for this workload.
                    </div>
                  )}
                </>
              );
            })()}

            {/* Data source info */}
            <div className="text-center mt-3">
              <small className="text-muted">
                Data source: {metrics.metadata.source} | Cache TTL: {metrics.metadata.ttl}s
              </small>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
